<template>
  <div class="dropbox-status-bar">
    <spinner v-if="loading"></spinner>
    <small class="status-bar d-block text-right" :class="{ 'working': dropboxStatus.saving || loading }"
      v-if="dropboxStatus.name">
      <div class="d-inline-block" :class="hebrew ? 'ml-4' : 'mr-4'">
        <i :title="prevPage ? prevPage.name : ''" @click="switchLoadedPage(-1)"
          :class="{ 'disabled pe-none': !prevPage || dropboxStatus.saving || loading, 'cursor-pointer': prevPage, 'fa-caret-right': hebrew, 'fa-caret-left': !hebrew }"
          class="fas">
        </i>
        <span class="file-name d-inline-block">
          <i class="fas fa-file-alt"></i>
          {{ dropboxStatus.name.trim() }}
        </span>
        <i :title="nextPage ? nextPage.name : ''" @click="switchLoadedPage(1)"
          :class="{ 'disabled pe-none': !nextPage || dropboxStatus.saving || loading, 'cursor-pointer': nextPage, 'fa-caret-right': !hebrew, 'fa-caret-left': hebrew }"
          class="fas">
        </i>
      </div>
      <div class="d-inline-block cursor-pointer project-name-wrap"
        :class="{ 'disabled pe-none': dropboxStatus.saving || loading }" v-if="getDirectory(dropboxStatus.path_display)"
        @click="goToProject">
        <i class="fas fa-folder"></i>
        <span class="project-name" :class="hebrew ? 'mr-1' : 'ml-1'">
          {{ getDirectory(dropboxStatus.path_display).trim() }}
        </span>
      </div>
      <div class="d-inline-block mx-4">
        <b-form-checkbox switch :disabled="dropboxStatus.saving || loading" v-model="dropboxMarkCompleted"
          @change="saveFile">
          <span class="d-inline-block">
            {{ hebrew ? 'פרוייקט הושלם' : 'Mark Completed' }}
          </span>
        </b-form-checkbox>
      </div>
      <div v-if="dropboxStatus.saving" class="d-inline-block">
        <i :class="{ 'fab fa-dropbox': !morphPartners, 'fas fa-save': morphPartners }"></i>
        {{ hebrew ? "מבצע שמירה..." : "Saving..." }}
      </div>
      <div class="d-inline-block" v-if="showError && !dropboxStatus.saving">
        <i :class="{ 'fab fa-dropbox': !morphPartners, 'fas fa-save': morphPartners }"></i>
        {{ hebrew ? "השמירה נכשלה" : "Failed to save..." }}
      </div>
      <div v-if="timePassed && !dropboxStatus.saving && !showError" class="d-inline-block">
        <i :class="{ 'fab fa-dropbox': !morphPartners, 'fas fa-save': morphPartners }"></i>
        {{ hebrew ? 'נשמר לפני' : 'Saved' }}
        {{ timePassed / 60 >= 1 ? Math.trunc(timePassed / 60) : 1 }}
        {{ hebrew ? "דק'" : "min ago" }}
      </div>
    </small>
    <FormSaveError :showForm="error !== null" :error-msg="error" :showDownload="false"
      @clear-error="showError = true; error = null" @call-next="runCallbackFunc">
    </FormSaveError>
  </div>
</template>
<script>
import { dropboxSave, getDisplayFolderFromPath, getAutoSaveInterval, getFolder, watchFolderWithStatus } from "shared/dropbox/dropbox_utils";
import { Mutations } from 'shared/dropbox/store'
import FormSaveError from 'shared/dropbox/FormSaveError'
import Spinner from 'shared/components/spinner'
import dropboxMixins from 'shared/dropbox/dropboxMixins'
let stopWatching = null

export default {
  name: 'StatusBar',
  data() {
    return {
      interval: null,
      timePassed: null,
      timer: 0,
      showError: false,
      dropboxMarkCompleted: this.$store.state.dropbox.status?.dictaStatus === 'COMPLETED',
      error: null,
      callbackFunc: ''
    }
  },
  components: { Spinner, FormSaveError },
  mixins: [dropboxMixins],
  props: ["useEdited"],
  created() {
    this.interval = setInterval(() => {
      this.getTime()
    }, 1000)
  },
  beforeDestroy() {
    if (stopWatching) {
      stopWatching()
      stopWatching = null
    }
  },
  mounted() {
    //when reloading file, reset last saved to prevent autosave on load
    this.$nextTick(() => {
      if (this.dropboxStatus.lastSaved) {
        const newStatus = Object.assign({}, this.dropboxStatus)
        newStatus.lastSaved = null
        this.$store.commit(Mutations.SET_DROPBOX_STATUS, newStatus)
      }
    })
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    runCallbackFunc() { //continue with action despite save failing
      if (this.callbackFunc === 'goToProject') {
        const currentPath = this.$store.state.dropbox?.status?.path_display
        this.$router.push('/projects' + getDisplayFolderFromPath(currentPath))
      } else if (this.callbackFunc === 'next') {
        this.switchPage(1)
      } else if (this.callbackFunc === 'prev') {
        this.switchPage(-1)
      }
      this.callbackFunc = ''
    },
    switchLoadedPage(direction) {
      clearInterval(this.interval)
      if (!this.useEdited || this.dropboxStatus.edited) {
        this.switchLoadedPageAndSave(direction)
      } else {
        this.switchPage(direction)
      }
    },
    switchLoadedPageAndSave(direction) {
      this.$store.commit(Mutations.SET_DROPBOX_LOADING, true)
      dropboxSave(this.dropboxStatus, this.dropboxMarkCompleted, false)
        .then(() => {
          this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
          this.switchPage(direction)
        })
        .catch(e => {
          this.error = e
          this.showError = true
          this.callbackFunc = direction == 1 ? 'next' : 'prev'
          this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
        })
    },
    saveFile() {
      this.$nextTick(() => {
        this.saveToDropbox(false)
      })
    },
    getTime() {
      if (!this.timePassed)
        this.timer++
      //use timer for initial save
      if (!this.timePassed && this.timer > getAutoSaveInterval() / 1000
        || this.timePassed > getAutoSaveInterval() / 1000) {
        //autosave, prevent spinner
        if (!this.dropboxStatus.saving && (!this.useEdited || this.dropboxStatus.edited)) {
          if (!this.timePassed) {
            this.timer = 0
          }
          else {
            //prevent double saving for smaller files with quicker saves
            if ((new Date().getTime() - this.dropboxStatus.lastSaved) / 1000 > 100)
              this.saveToDropbox(true)
          }
        }
      }
      if (this.dropboxStatus.lastSaved)
        this.timePassed = (new Date().getTime() - this.dropboxStatus.lastSaved) / 1000
      else
        this.timePassed = null
    },
    saveToDropbox(hideSpinner) {
      if (!hideSpinner)
        this.$store.commit(Mutations.SET_DROPBOX_LOADING, true)
      this.showError = false
      dropboxSave(this.dropboxStatus, this.dropboxMarkCompleted, false)
        .then(() => {
          this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
        }).catch(e => {
          this.showError = true
          if (!hideSpinner)
            this.error = e
          this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
        })
    },
    getDirectory(path) {
      let pathArr = path.split('/')
      // hide the folder name if would be the userId ("root") folder
      if (process.env.VUE_APP_MORPHOLOGY_PARTNERS && pathArr.length < 5)
        return ''
      if (process.env.VUE_APP_OCR_PARTNERS && pathArr.length < 3)
        return ''
      return pathArr[pathArr.length - 2]
    },
    goToProject() {
      if (!this.useEdited || this.dropboxStatus.edited)
        this.goToProjectAndSave()
      else {
        const currentPath = this.$store.state.dropbox?.status?.path_display
        this.$router.push('/projects' + getDisplayFolderFromPath(currentPath))
      }
    },
    goToProjectAndSave() {
      if (!this.dropboxStatus.saving) {
        this.$store.commit(Mutations.SET_DROPBOX_LOADING, true)
        dropboxSave(this.dropboxStatus, this.dropboxMarkCompleted, false)
          .then(() => {
            const currentPath = this.$store.state.dropbox?.status?.path_display
            this.$router.push('/projects' + getDisplayFolderFromPath(currentPath))
            this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
          })
          .catch(e => {
            this.error = e
            this.showError = true
            this.callbackFunc = 'goToProject'
            this.$store.commit(Mutations.SET_DROPBOX_LOADING, false)
          })
      }
      //})
    }
  },
  computed: {
    hebrew() {
      return this.$settings && this.$settings.hebrew
    },
    dropboxStatus() {
      return this.$store.state.dropbox.status
    },
    morphPartners() {
      return process.env.VUE_APP_MORPHOLOGY_PARTNERS
    }
  },
  watch: {
    dropboxStatus: {
      handler(newStatus, oldStatus) {
        if (newStatus?.path_display) {
          // we might have the same file, but with an updated status, but then there's no need to change the watch
          if(newStatus.path_display !== oldStatus?.path_display) {
            this.timer = 0
            this.showError = false
            this.dropboxMarkCompleted = this.dropboxStatus.dictaStatus === 'COMPLETED'
            if (stopWatching) stopWatching()
            stopWatching =
                watchFolderWithStatus(getFolder(newStatus.path_display), (entries) => this.updateFolderStatus(entries))
          }
        } else {
          if (stopWatching) {
            stopWatching()
            stopWatching = null
          }
        }
      },
      immediate: true
    }
  }
}
</script>
<style lang="scss" scoped>
.status-bar {
  &.working {
    opacity: 0.5;
  }

  padding: 6px 0;

  .file-name {
    margin: 0 10px;
  }

  color: #8d8d8d;

  //font-size: 13px;
  .d-inline-block,
  i {
    vertical-align: middle;
  }

  i {
    color: #4a5057;
    font-size: 17px;

    &.fa-caret-left,
    &.fa-caret-right {
      font-size: 27px;
      color: #8d8d8d;
      top: -2px;
      position: relative;

      &:before {
        position: relative;
        z-index: 1;
        top: 2px;
      }

      &:after {
        content: '';
        position: absolute;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        left: -10px;
        top: 3px;
        background: transparent;
      }

      &.disabled {
        color: #d8d8d8;
      }

      &:hover {
        color: #4a5057;

        &:after {
          background: #e3e3e3;
        }
      }
    }

    &.fa-caret-left {
      &:after {
        left: -6px;
      }
    }
  }

  .project-name-wrap {
    &:hover {
      .project-name {
        text-decoration: underline;
      }
    }
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .pe-none {
    pointer-events: none;
  }
}
</style>
<style>
.status-bar .custom-control-label {
  vertical-align: middle
}
</style>
